import { useMemo } from "react"
import type { OperationContext } from "urql"
import { useQuery } from "urql"

import type {
  CompanyCertification,
  CompanyCertificationListSuccess,
  CompanyCertificationType,
  Query,
} from "@/graphql/codegen/graphql"
import { CompanyCertificationListDocument } from "@/graphql/codegen/graphql"
import type { GroupedByCertificationType } from "@/screens/Companies/edit/components/certification/types"
import { groupByTypeAndLocation } from "@/screens/Companies/edit/components/certification/utils"

interface UseGetCompanyCertificationProps {
  companyId: string
  certificationType?: CompanyCertificationType
  isExpired?: boolean
}

interface UseCompanyCertificationReturn {
  companyCertifications: CompanyCertification[]
  groupedCompanyCertifications: GroupedByCertificationType[]
  fetching: boolean
  executeQuery: (opts?: Partial<OperationContext>) => void
}

const useGetCompanyCertification = ({
  companyId,
  certificationType,
  isExpired,
}: UseGetCompanyCertificationProps): UseCompanyCertificationReturn => {
  const [{ data, fetching }, executeQuery] = useQuery<Pick<Query, "companyCertification">>({
    query: CompanyCertificationListDocument,
    variables: { input: { companyId, certificationType, isExpired } },
  })

  const companyCertifications: CompanyCertification[] = useMemo(() => {
    return (
      ((data?.companyCertification?.list as CompanyCertificationListSuccess)
        ?.companyCertification as CompanyCertification[]) ?? []
    )
  }, [data])

  const groupedCompanyCertifications: GroupedByCertificationType[] = useMemo(() => {
    return groupByTypeAndLocation(companyCertifications)
  }, [companyCertifications])

  return {
    companyCertifications,
    groupedCompanyCertifications,
    fetching,
    executeQuery,
  }
}

export default useGetCompanyCertification
